'use client';

import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  FeatureFlagName,
  FeatureFlags,
} from '@/core/models/feature-flags';
import DEFAULT_FEATURE_FLAGS from '@/core/constants/feature-flags';
import parseBoolean from '@/ui/utils/parseBoolean/parseBoolean';

type FeatureFlagContextType = {
  flags: FeatureFlags;
  isEnabled: (flag: FeatureFlagName) => boolean;
  setFlags: (overrides: Partial<FeatureFlags>) => void;
};

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagProvider',
    );
  }
  return context;
};

export const getDefaultFeatureFlags = (): FeatureFlags => ({
  FEATURE_ACCESSORIES: parseBoolean(DEFAULT_FEATURE_FLAGS.FEATURE_ACCESSORIES),
  FEATURE_RETURN_FLOW: parseBoolean(DEFAULT_FEATURE_FLAGS.FEATURE_RETURN_FLOW),
  FEATURE_STORAGE_BAG: parseBoolean(DEFAULT_FEATURE_FLAGS.FEATURE_STORAGE_BAG),
  FEATURE_BOOKING_TOOL: parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_BOOKING_TOOL,
  ),
  FEATURE_CUSTOMER_SERVICE_FORM: parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_CUSTOMER_SERVICE_FORM,
  ),
  FEATURE_PRODUCTS_SLIDER: parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_PRODUCTS_SLIDER,
  ),
  FEATURE_RELAXATION_MASK: parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_RELAXATION_MASK,
  ),
  FEATURE_APP_PAGE: parseBoolean(DEFAULT_FEATURE_FLAGS.FEATURE_APP_PAGE),
  FEATURE_EXPANSION_MARKETS_ORDER_FLOW: parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_EXPANSION_MARKETS_ORDER_FLOW,
  ),
  FEATURE_LIFETIME_SUBSCRIPTION: parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_LIFETIME_SUBSCRIPTION,
  ),
  FEATURE_ACCOUNT_SUBSCRIPTION_ACTIONS: parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_ACCOUNT_SUBSCRIPTION_ACTIONS,
  ),
  FEATURE_SOCIAL_LOGIN: parseBoolean(
    DEFAULT_FEATURE_FLAGS.FEATURE_SOCIAL_LOGIN,
  ),
  FEATURE_MAGAZINE: parseBoolean(DEFAULT_FEATURE_FLAGS.FEATURE_MAGAZINE),
});

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const [flags, setFlagsState] = useState<FeatureFlags>(getDefaultFeatureFlags);

  const setFlags = useCallback((overrides: Partial<FeatureFlags>) => {
    setFlagsState(() => ({
      ...getDefaultFeatureFlags(),
      ...overrides,
    }));
  }, []);

  const isEnabled = useCallback(
    (flag: FeatureFlagName) => flags[flag],
    [flags],
  );

  const value = useMemo(
    () => ({
      flags,
      isEnabled,
      setFlags,
    }),
    [flags, isEnabled, setFlags],
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
